import React, { useEffect, useState } from "react";

import { Tab, Tabs, makeStyles } from "@material-ui/core";
import { TabContext, TabPanel } from "@material-ui/lab";

import {
  DESKTOP_EXPERIMENT_BANNER_HEIGHT,
  DESKTOP_TOP_BAR_HEIGHT,
  MOBILE_TOP_BAR_HEIGHT,
} from "@hubcapp/constants";
import PageHeader from "@ds-developer/page-header";

import { MessagesLayoutProps } from "./types";
import ChatInput from "@ds-developer/chat-input";
import ChatList from "@ds-developer/chat-list";
import NewsFeed from "../NewsFeed";
import TechUpdates from "../TechUpdates";

const CHAT_LIST_MAX_WIDTH = 1100;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "100%",
    minHeight: `calc(100vh - ${
      DESKTOP_TOP_BAR_HEIGHT +
      (window?._env_?.DISPLAY_EXPERIMENT_BANNER
        ? DESKTOP_EXPERIMENT_BANNER_HEIGHT
        : 0)
    }px)`,

    [theme.breakpoints.down("xs")]: {
      minHeight: `calc(100vh - ${MOBILE_TOP_BAR_HEIGHT}px)`,
    },
  },
  panelWrapper: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3, 2),
    [theme.breakpoints.up("lg")]: {
      padding: theme.spacing(4, 5),
    },
  },
  chatContainer: {
    display: "flex",
    flex: 1,
    height: "100%",
    marginBottom: "64px",
    flexDirection: "column",
  },
  tabPanel: {
    display: "flex",
    height: "100%",
  },
  listContainer: {
    display: "flex",
    width: "100%",
    flex: 1,
    maxWidth: CHAT_LIST_MAX_WIDTH,
    margin: "0 auto",
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up("lg")]: {
      padding: theme.spacing(0, 5),
    },
  },
  inputContainer: {
    background: "white",
    width: "100%",
    position: "fixed",
    bottom: 0,
    border: "1px solid #E0E0E0",
  },
  input: {
    maxWidth: CHAT_LIST_MAX_WIDTH,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: "calc(100% - 40px)",
      margin: theme.spacing(0, 2),
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "calc(100% - 272px)",
      margin: theme.spacing(0, 2),
    },
  },
}));

const MessagesLayout: React.FC<MessagesLayoutProps> = ({
  onTabChange,
  selectedTab,
  PageHeaderProps,
  messages = [],
  message,
  setMessage,
  sendMessage,
  loadMore,
  moreMessagesAvailable,
  instructorName,
}) => {
  const classes = useStyles();
  const handleChange = (_: unknown, newValue: string) => {
    onTabChange(newValue);
  };

  const [height, setHeight] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const listRef: React.RefObject<HTMLDivElement> = React.createRef();
  const chatInputProps = {
    handleChange: setMessage,
    handleSend: sendMessage,
    placeholder: "Send a message",
    senderName: instructorName,
    value: message,
  };

  const chatListProps = {
    height: height,
    messages: messages,
    width: width,
    loadMore: loadMore,
    moreMessagesAvailable: moreMessagesAvailable,
  };

  useEffect(() => {
    if (!listRef.current || (height > 0 && width > 0)) return;
    setHeight(listRef.current.clientHeight);
    setWidth(listRef.current.clientWidth);
  }, [selectedTab]);

  return (
    <div className={classes.root}>
      <div className={classes.panelWrapper}>
        <PageHeader {...PageHeaderProps} />
        <Tabs
          indicatorColor="secondary"
          onChange={handleChange}
          scrollButtons="auto"
          textColor="secondary"
          value={selectedTab}
          variant="scrollable"
        >
          <Tab
            data-test="messages-tab-messages"
            label="Messages"
            value="messages"
          />
          <Tab data-test="messages-tab-news" label="News" value="news" />
          <Tab
            data-test="messages-tab-tech-updates"
            label="Tech Updates"
            value="tech-updates"
          />
        </Tabs>

        <TabContext value={selectedTab}>
          <TabPanel value="news">
            <NewsFeed />
          </TabPanel>
          <TabPanel value="tech-updates">
            <TechUpdates />
          </TabPanel>
        </TabContext>
      </div>
      {selectedTab == "messages" && (
        <div className={classes.chatContainer}>
          <div ref={listRef} className={classes.listContainer}>
            {height !== 0 && (
              <ChatList data-test={"chat-list"} {...chatListProps} />
            )}
          </div>
        </div>
      )}
      {selectedTab == "messages" && (
        <div className={classes.inputContainer}>
          <div className={classes.input}>
            <ChatInput {...chatInputProps} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesLayout;
